import type { CSSProperties } from 'react';
import { api } from '../lib/api';
import { useCatalog } from '../lib/catalog';
import { clock, minutes } from '../lib/format';
import { useQuery } from '../lib/query';
import { Link, back } from '../lib/router';
import { hasPlus, setState, toast, useApp } from '../lib/store';
import { Icon } from '../components/Icon';
import { PosterArt } from '../components/Poster';
import { AppShell, ErrorBox, SkeletonRails } from '../components/Shell';
import { showSummary } from '../lib/showIndex';
import type { ShowDetail } from '../lib/types';

export function ShowPage({ slug }: { slug: string }) {
  const { topics: TOPICS } = useCatalog();
  const { data: detail, error, loading, reload } = useQuery('show:' + slug, () => api.show(slug));
  // Paint instantly from the summary the list already had; episodes fill in when details arrive.
  const summary = detail ? undefined : showSummary(slug);
  const show: ShowDetail | null | undefined = detail ?? (summary ? { ...summary, episodes: [] } : detail);
  const pending = !detail && !!summary;
  const plus = useApp((s) => hasPlus(s));
  const progress = useApp((s) => s.progress);
  const saved = useApp((s) => (show ? !!s.saves[show.id] : false));

  if (loading && !show && !summary) return <AppShell><div className="page"><SkeletonRails rails={1} hero /></div></AppShell>;
  if (error) return <AppShell><ErrorBox message={error} onRetry={reload} /></AppShell>;
  if (!show) return <AppShell><ErrorBox message="We could not find that show." /></AppShell>;

  const topic = TOPICS.find((t) => t.slug === show.topic);
  const total = show.episodes.reduce((a, e) => a + e.durationS, 0);
  const resume = [...show.episodes].reverse().find((e) => progress[e.id] && progress[e.id].positionS < e.durationS - 5);
  const startEp = resume || show.episodes[0];
  const freeCount = show.episodes.filter((e) => e.isFreePreview).length;

  const toggleSave = () => {
    setState((s) => {
      const saves = { ...s.saves };
      if (saves[show.id]) delete saves[show.id]; else saves[show.id] = true;
      return { saves };
    });
    toast(saved ? 'Removed from your library' : 'Saved to your library');
  };
  const share = () => {
    const url = `https://kuchnaya.in/show/${show.slug}`;
    navigator.clipboard?.writeText(url).then(() => toast('Link copied'), () => toast(url));
  };

  return (
    <AppShell mobileHeader={false}>
      <div className="show-page">
        <header className="show-hero" style={{ '--poster': show.color } as CSSProperties}>
          <div className="show-hero-bar">
            <button type="button" className="icon-btn glass" onClick={() => back('/home')} aria-label="Back"><Icon name="back" /></button>
            <span className="row">
              <button type="button" className="icon-btn glass" onClick={toggleSave} aria-pressed={saved} aria-label={saved ? 'Remove from library' : 'Save to library'}>
                <Icon name="bookmark" />
              </button>
              <button type="button" className="icon-btn glass" onClick={share} aria-label="Share"><Icon name="share" /></button>
            </span>
          </div>
          <div className="show-hero-body">
            <span className="hero-poster-vt"><PosterArt show={show} size="md" /></span>
            <div className="show-hero-text">
              <span className="eyebrow">{topic?.name}</span>
              <h1>{show.title}</h1>
              <span className="creator"><span className="avatar sm">{show.creator.name[0]}</span>With {show.creator.name}</span>
              <span className="muted small">{pending ? show.episodeCount : show.episodes.length} episodes{pending ? '' : ` · ${minutes(total)}`} · {show.videoLanguage}{show.videoLanguage === 'Hindi' ? ' with English subtitles' : ''}</span>
            </div>
          </div>
        </header>

        <div className="show-body">
          <Link to={`/watch/${show.id}/${startEp?.no ?? 1}`} className="btn btn-lg btn-primary btn-block">
            <Icon name="play" size={16} /> {resume ? `Continue Ep ${resume.no}` : 'Watch Ep 1 free'}
          </Link>
          <p className="show-desc">{show.description} {show.access === 'plus' && !plus && freeCount > 0 && <>The first {freeCount === 1 ? 'episode is' : `${freeCount} episodes are`} free.</>}</p>

          <h2 className="rail-title">Episodes</h2>
          {pending && (
            <ol className="episodes" aria-busy="true" aria-label="Loading episodes">
              {Array.from({ length: Math.min(show.episodeCount || 4, 6) }, (_, i) => (
                <li key={i} className="episode skel-episode"><span className="skel-block ep-thumb" /><span className="ep-text"><span className="skel-block skel-line w60" /><span className="skel-block skel-line w40" /></span></li>
              ))}
            </ol>
          )}
          <ol className="episodes">
            {show.episodes.map((e) => {
              const p = progress[e.id];
              const locked = show.access === 'plus' && !e.isFreePreview && !plus;
              return (
                <li key={e.id}>
                  <Link to={locked ? `/plus?show=${show.id}&ep=${e.no}` : `/watch/${show.id}/${e.no}`} className="episode">
                    <span className="ep-thumb" style={{ '--poster': show.color } as CSSProperties}>{e.no}</span>
                    <span className="ep-text">
                      <b>{e.title}</b>
                      <small>{clock(e.durationS)}{p ? ` · ${clock(p.positionS)} watched` : ''}</small>
                      {p && <span className="ep-progress"><span style={{ width: `${Math.min(100, (p.positionS / e.durationS) * 100)}%` }} /></span>}
                    </span>
                    {locked ? <span className="tag-plus"><Icon name="lock" size={14} /> PLUS</span> : <span className="tag-free">{show.access === 'free' ? 'FREE' : e.isFreePreview ? 'FREE' : ''}</span>}
                  </Link>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </AppShell>
  );
}
